import React from 'react'

import { Link, useNavigate } from 'react-router-dom';
/*
 * IMAGE PLACEMENT INSTRUCTIONS:
 * 1. Hero Video: 'images/backgrounds/herovideo.mp4'
 * 2. Section Images: 'images/backgrounds/StatisticsImpact.jpg', 'HowItWorks MarketplaceFlow.jpg', etc.
 * 3. Partner/Trust Logos: Place logos in 'images/partners/' and update the Trust Section below.
 */

// Helper component for scroll-triggered fade-in animations
const FadeInSection = ({ children, className = "", delay = "0ms" }) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const domRef = React.useRef();

    React.useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) setIsVisible(true);
            });
        });
        const currentElement = domRef.current;
        if (currentElement) observer.observe(currentElement);
        return () => {
            if (currentElement) observer.unobserve(currentElement);
        };
    }, []);

    return (
        <div
            ref={domRef}
            className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`}
            style={{ transitionDelay: delay }}
        >
            {children}
        </div>
    );
};

// Helper component for animated statistics counters
const AnimatedCounter = ({ end, suffix = "", duration = 2000 }) => {
    const [count, setCount] = React.useState(0);
    const [isVisible, setIsVisible] = React.useState(false);
    const domRef = React.useRef();

    React.useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) setIsVisible(true);
            });
        });
        const currentElement = domRef.current;
        if (currentElement) observer.observe(currentElement);
        return () => {
            if (currentElement) observer.unobserve(currentElement);
        };
    }, []);
    
    React.useEffect(() => {
        if (isVisible) {
            let startTime;
            const step = (timestamp) => {
                if (!startTime) startTime = timestamp;
                const progress = Math.min((timestamp - startTime) / duration, 1);
                setCount(Math.floor(progress * end));
                if (progress < 1) {
                    window.requestAnimationFrame(step);
                }
            };
            window.requestAnimationFrame(step);
        }
    }, [isVisible, end, duration]);

    return <span ref={domRef}>{count}{suffix}</span>;
};

// Custom styles for the shine effect
const customStyles = `
    @keyframes shimmer {
        0% { background-position: -200% center; }
        100% { background-position: 200% center; }
    }
    .animate-shimmer {
        background: linear-gradient(to right, #60a5fa 20%, #e0f2fe 40%, #60a5fa 60%);
        background-size: 200% auto;
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        animation: shimmer 3s linear infinite;
    }
`;

// --- NEW: Modal to prompt mobile web users to download the app ---
export default function UseWebVersionModal({ isOpen, onClose, onConfirm }) {
    if (!isOpen) return null;

    // Detect iOS to provide the correct App Store link
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const appStoreLink = isIOS 
        ? 'https://apps.apple.com/za/app/miners-market/id6753230270' 
        : 'https://play.google.com/store/apps/details?id=za.co.minersmarket.twa&hl=en-US&pli=1';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100] p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center">
                <div className="text-blue-500 mb-4">
                    <i className="fas fa-mobile-alt fa-3x"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Get the Best Experience</h2>
                <p className="text-gray-600 mb-6">
                    Our native app is faster and more convenient. Signing up and using the app is free.
                </p>
                <div className="space-y-3">
                    <a 
                        href={appStoreLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full block bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors text-lg"
                    >
                        Download Now
                    </a>
                    <button 
                        onClick={onConfirm}
                        className="w-full bg-gray-200 text-gray-800 font-medium py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Continue on Web
                    </button>
                </div>
                 <button onClick={onClose} className="mt-6 text-sm text-gray-500 hover:text-gray-700">
                    Cancel
                </button>
            </div>
        </div>
    );
}

// --- NEW: Hook to check for mobile viewport ---
const useIsMobile = () => {
    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
};

function MarketingPage({ onLogin, onSignUp, onViewProduct, user, basket, onAddToBasket, onUpdateBasketQuantity, onRemoveFromBasket }) {
    // --- NEW: Add navigation and state for mobile prompt ---
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [showWebConfirm, setShowWebConfirm] = React.useState(false);

    const handleUseOnWeb = () => {
        if (isMobile) {
            setShowWebConfirm(true);
        } else {
            // For desktop, navigate directly to the app's main marketplace page
            navigate('/equipment');
        }
    };

    return (
        <div data-name="marketing-page" className="min-h-screen bg-gray-900 text-gray-100 font-sans selection:bg-blue-500 selection:text-white">
            <style>{customStyles}</style>
            
            {/* 1. HERO SECTION (ID: home) */}
            <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
                {/* Parallax Background */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gray-900/70 z-10"></div>
                    <video autoPlay loop muted playsInline className="w-full h-full object-cover">
                        <source src="images/backgrounds/herovideo.mp4" type="video/mp4" />
                    </video>
                </div>

                <div className="container mx-auto px-4 relative z-20 pt-20">
                    <div className="max-w-5xl mx-auto text-center">
                        <FadeInSection>
                            <h1 className="text-6xl md:text-8xl font-extrabold mb-8 tracking-tight text-white drop-shadow-2xl">
                                MINERS <span className="animate-shimmer">MARKET</span>
                            </h1>
                            <h2 className="text-3xl md:text-4xl font-light text-gray-200 mb-8">
                                The Digital Marketplace Built for the <span className="text-blue-400 font-semibold">Mining Industry</span>
                            </h2>
                            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
                                A secure, industry-focused platform to buy, sell, and source mining equipment, parts, ores, and services.
                                Miners Market simplifies mining procurement by connecting verified buyers and sellers in one trusted digital environment. Signing up and using the platform is completely free.
                            </p>
                            
                            <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
                                <button 
                                    onClick={handleUseOnWeb}
                                    className="bg-blue-700 hover:bg-blue-600 text-white font-bold py-5 px-10 rounded-lg text-xl transition-all transform hover:-translate-y-1 shadow-lg shadow-blue-900/50 w-full md:w-auto border border-blue-500"
                                >
                                    Use Miners Market on Web
                                </button>
                                <div className="flex gap-4 w-full md:w-auto justify-center">
                                    <button
                                        onClick={() => openExternalLink('https://apps.apple.com/za/app/miners-market/id6753230270')}
                                        className="bg-gray-800 border border-gray-600 hover:bg-gray-700 text-white p-4 rounded-lg flex items-center gap-3 transition-all hover:border-blue-400"
                                    >
                                        <i className="fab fa-apple text-2xl"></i>
                                        <div className="text-left">
                                            <div className="text-xs text-gray-400">Download on the</div>
                                            <div className="text-sm font-bold leading-none">App Store</div>
                                        </div>
                                    </button>
                                    <button 
                                        onClick={() => openExternalLink('https://play.google.com/store/apps/details?id=za.co.minersmarket.twa&hl=en-US&pli=1')}
                                        className="bg-gray-800 border border-gray-600 hover:bg-gray-700 text-white p-4 rounded-lg flex items-center gap-3 transition-all hover:border-blue-400"
                                    >
                                        <i className="fab fa-google-play text-2xl"></i>
                                        <div className="text-left">
                                            <div className="text-xs text-gray-400">Get it on</div>
                                            <div className="text-sm font-bold leading-none">Google Play</div>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </FadeInSection>
                    </div>
                </div>
                
                {/* Scroll Indicator */}
                <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce text-gray-500">
                    <i className="fas fa-chevron-down text-2xl"></i>
                </div>
            </section>

            {/* 2. KEY INDUSTRY STATISTICS (Immediate Proof) */}
            <section className="bg-blue-900 py-12 border-b border-blue-800 relative z-20">
                 <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row items-center gap-12">
                        <div className="w-full md:w-1/3">
                            <img src="images/backgrounds/StatisticsImpact.jpg" alt="Industry Impact" className="rounded-lg shadow-2xl border-4 border-blue-800/50" />
                        </div>
                        <div className="w-full md:w-2/3 grid grid-cols-2 gap-8 text-center">
                            <FadeInSection delay="0ms">
                                 <div className="text-3xl md:text-5xl font-bold text-white mb-2"><AnimatedCounter end={1500} suffix="+" /></div>
                                 <div className="text-blue-200 text-sm font-bold uppercase tracking-wider">Active Listings</div>
                            </FadeInSection>
                            <FadeInSection delay="100ms">
                                 <div className="text-3xl md:text-5xl font-bold text-white mb-2"><AnimatedCounter end={450} suffix="+" /></div>
                                 <div className="text-blue-200 text-sm font-bold uppercase tracking-wider">Verified Sellers</div>
                            </FadeInSection>
                            <FadeInSection delay="200ms">
                                 <div className="text-3xl md:text-5xl font-bold text-white mb-2"><AnimatedCounter end={85} suffix="%" /></div>
                                 <div className="text-blue-200 text-sm font-bold uppercase tracking-wider">Faster Sourcing</div>
                            </FadeInSection>
                            <FadeInSection delay="300ms">
                                 <div className="text-3xl md:text-5xl font-bold text-white mb-2"><AnimatedCounter end={100} suffix="%" /></div>
                                 <div className="text-blue-200 text-sm font-bold uppercase tracking-wider">Secure Escrow</div>
                            </FadeInSection>
                        </div>
                    </div>
                 </div>
            </section>

            {/* 3. TRUST SIGNALS (Industry Legitimacy) */}
            <section className="py-16 bg-white border-b border-gray-200">
                <div className="container mx-auto px-4 text-center">
                    <div>
                        <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-6">Powered By</p>
                        <div className="flex flex-wrap justify-center items-center gap-12 opacity-70">
                            <a href="https://wotsgroup.org.za/" target="_blank"><img src="images/home/WOTS-Group-Logo-PNG.webp" alt="WOTS Group" className="h-12 object-contain grayscale hover:grayscale-0 transition-all" /></a>
                            <a href="https://www.tradesafe.co.za/" target="_blank"><img src="images/home/tradesafe3.png" alt="TradeSafe" className="h-10 object-contain grayscale hover:grayscale-0 transition-all" /></a>
                            <a href="https://vigilantapp.co.za/" target="_blank"><img src="images/home/vigilantlogo.png" alt="Vigilant" className="h-10 object-contain grayscale hover:grayscale-0 transition-all" /></a>
                            <a href="https://slascanh.co.za/" target="_blank"><img src="images/home/SLASCANH-Logo.png" alt="SLASCANH" className="h-12 object-contain grayscale hover:grayscale-0 transition-all" /></a>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3.5 TRADESAFE TRUST SECTION */}
            <section className="py-20 bg-gray-50 border-b border-gray-200">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <img src="images/home/tradesafe3.png" alt="TradeSafe" className="h-12 mx-auto mb-6" />
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Trusted Secure Payments with TradeSafe</h2>
                            <p className="text-lg text-gray-600">
                                At Miners Market, protecting your transactions — both buying and selling — is a top priority. That’s why we partner with TradeSafe, one of South Africa’s most trusted digital escrow payment providers.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-12 mb-12">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">How It Works</h3>
                                <ul className="space-y-4">
                                    {[
                                        "The buyer and seller agree on the terms of the transaction.",
                                        "The buyer deposits the funds securely with TradeSafe’s escrow service.",
                                        "The seller delivers the goods or service.",
                                        "Once delivery is confirmed, TradeSafe releases the payment to the seller."
                                    ].map((item, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">{i+1}</div>
                                            <span className="text-gray-700">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Why It Matters</h3>
                                <ul className="space-y-4">
                                    <li className="flex items-start gap-3">
                                        <i className="fas fa-university text-blue-600 mt-1"></i>
                                        <div>
                                            <span className="font-bold text-gray-900">Bank‑backed security:</span>
                                            <p className="text-gray-600 text-sm">Funds are held under a bank‑supported system to ensure integrity and trust.</p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <i className="fas fa-handshake text-blue-600 mt-1"></i>
                                        <div>
                                            <span className="font-bold text-gray-900">Neutral third party:</span>
                                            <p className="text-gray-600 text-sm">TradeSafe sits between buyer and seller, protecting both sides.</p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <i className="fas fa-shield-alt text-blue-600 mt-1"></i>
                                        <div>
                                            <span className="font-bold text-gray-900">Enhanced confidence:</span>
                                            <p className="text-gray-600 text-sm">Buyers and sellers can transact with peace of mind, knowing their funds are only released when all obligations are met.</p>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-100 p-6 rounded-xl text-center">
                            <p className="text-blue-800 font-medium">
                                Miners Market and TradeSafe together give you a secure, trusted, and transparent payment experience, so you can focus on growing your business without the stress of transactional risk.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. WHY MINERS MARKET EXISTS (ID: why-us) */}
            <section id="why-us" className="py-24 bg-gray-900 border-b border-gray-800">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <FadeInSection>
                            <div className="inline-block px-4 py-2 border border-blue-500/30 rounded-full text-blue-400 text-sm font-bold tracking-wide mb-6">
                                THE PROBLEM
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
                                Why Miners Market Was Created
                            </h2>
                            <p className="text-gray-300 text-xl leading-relaxed mb-8">
                                Mining procurement has traditionally relied on fragmented networks, manual processes, and informal channels. This creates delays, increases costs, and limits visibility — especially in an industry where operational downtime is expensive.
                            </p>
                            <p className="text-gray-300 text-xl leading-relaxed">
                                Miners Market was built to modernise this process by providing a single, digital marketplace dedicated exclusively to the mining sector.
                            </p>
                        </FadeInSection>
                        
                        <FadeInSection delay="200ms">
                            <img src="images/backgrounds/HowItWorks MarketplaceFlow.jpg" alt="Marketplace Flow" className="rounded-2xl shadow-2xl border border-gray-700 hover:scale-105 transition-transform duration-500" />
                        </FadeInSection>
                    </div>
                </div>
            </section>

            {/* 5. WHAT IS MINERS MARKET */}
            <section className="py-24 bg-gray-800">
                <div className="container mx-auto px-4">
                    <FadeInSection>
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">What Is Miners Market?</h2>
                            <p className="text-gray-300 text-xl">
                                Miners Market is a specialised online marketplace designed for the mining industry. It enables businesses and professionals to list, discover, and transact mining-related assets and services with confidence.
                            </p>
                        </div>
                    </FadeInSection>

                    <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {[
                            { icon: 'fa-truck-monster', title: 'Mining Equipment' },
                            { icon: 'fa-cogs', title: 'Spare Parts' },
                            { icon: 'fa-oil-can', title: 'Consumables' },
                            { icon: 'fa-gem', title: 'Ores & Minerals' },
                            { icon: 'fa-tools', title: 'Services' }
                        ].map((item, idx) => (
                            <FadeInSection key={idx} delay={`${idx * 100}ms`}>
                                <div className="bg-gray-700 hover:bg-gray-600 p-8 rounded-xl text-center transition-colors border border-gray-600 hover:border-blue-500/50 group h-full flex flex-col items-center justify-center">
                                    <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform border border-gray-600 group-hover:border-blue-500">
                                        <i className={`fas ${item.icon} text-3xl text-gray-400 group-hover:text-blue-400 transition-colors`}></i>
                                    </div>
                                    <h3 className="font-bold text-lg text-gray-200 group-hover:text-white">{item.title}</h3>
                                </div>
                            </FadeInSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* 6. WHO IT'S FOR (ID: who-its-for) */}
            <section id="who-its-for" className="py-24 bg-gray-900">
                <div className="container mx-auto px-4">
                    <FadeInSection>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-16 text-center">Built for the Entire Mining Value Chain</h2>
                    </FadeInSection>
                    
                    <div className="flex flex-col lg:flex-row gap-12 items-center">
                        <div className="w-full lg:w-1/2 grid md:grid-cols-2 gap-6">
                            {[
                                { title: "Mining Houses & Operations", desc: "Streamline procurement of critical plant and machinery to minimize downtime." },
                                { title: "Equipment OEMs", desc: "Direct channel to sell new and refurbished units to verified mining buyers." },
                                { title: "Service Providers", desc: "Connect with mines needing maintenance, logistics, and specialized services." },
                                { title: "Parts Suppliers", desc: "List consumables and spares for a targeted, high-intent industrial audience." }
                            ].map((card, idx) => (
                                <FadeInSection key={idx} delay={`${idx * 100}ms`}>
                                    <div className="bg-gray-800 p-8 rounded-xl border-l-4 border-blue-600 hover:bg-gray-750 transition-colors h-full">
                                        <h3 className="text-xl font-bold text-white mb-3">{card.title}</h3>
                                        <p className="text-gray-400">{card.desc}</p>
                                    </div>
                                </FadeInSection>
                            ))}
                        </div>
                        <div className="w-full lg:w-1/2">
                            <FadeInSection delay="200ms">
                                <img src="images/backgrounds/WhoUsesMinersMarketAudience.jpg" alt="Who Uses Miners Market" className="rounded-2xl shadow-2xl border border-gray-700" />
                            </FadeInSection>
                        </div>
                    </div>
                </div>
            </section>

            {/* 7. THE INDUSTRY SHIFT */}
            <section className="py-24 bg-gradient-to-b from-gray-800 to-gray-900 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10 bg-[url('images/backgrounds/home-bg.jpg')] bg-cover bg-fixed"></div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <FadeInSection>
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">The Mining Industry Is Moving Online</h2>
                            <p className="text-gray-300 text-xl mb-10">
                                Across heavy industry, procurement and asset sourcing are rapidly shifting toward digital platforms. Mining is no exception.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <i className="fas fa-check-circle text-blue-500 text-xl"></i>
                                    <span className="text-gray-300 text-lg">Digital procurement adoption growing annually</span>
                                </div>
                                <div className="flex items-center gap-4">
                                    <i className="fas fa-check-circle text-blue-500 text-xl"></i>
                                    <span className="text-gray-300 text-lg">Faster sourcing reduces operational risk</span>
                                </div>
                            </div>
                        </FadeInSection>
                        
                        <div className="grid grid-cols-1 gap-6">
                            <FadeInSection delay="200ms">
                                <div className="bg-gray-900/80 backdrop-blur p-8 rounded-xl border border-gray-700 text-center">
                                    <div className="text-6xl font-bold text-blue-500 mb-2">
                                        <AnimatedCounter end={70} suffix="%" />
                                    </div>
                                    <p className="text-gray-400 uppercase tracking-wider text-sm">B2B Buyers Research Online</p>
                                </div>
                            </FadeInSection>
                            <FadeInSection delay="400ms">
                                <div className="bg-gray-900/80 backdrop-blur p-8 rounded-xl border border-gray-700 text-center">
                                    <div className="text-6xl font-bold text-blue-400 mb-2">
                                        <AnimatedCounter end={24} suffix="/7" />
                                    </div>
                                    <p className="text-gray-400 uppercase tracking-wider text-sm">Marketplace Availability</p>
                                </div>
                            </FadeInSection>
                        </div>
                    </div>
                </div>
            </section>

            {/* 8. KEY BENEFITS */}
            <section className="py-24 bg-gray-900">
                <div className="container mx-auto px-4">
                    <FadeInSection>
                        <div className="text-center mb-16">
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Why Industry Professionals Choose Miners Market</h2>
                        </div>
                    </FadeInSection>

                    <div className="flex flex-col lg:flex-row gap-12 items-center">
                        <div className="w-full lg:w-1/3 order-2 lg:order-1">
                             <video autoPlay loop muted playsInline className="w-full rounded-2xl shadow-2xl border border-gray-700">
                                <source src="images/backgrounds/BenefitsTrustSection.mp4" type="video/mp4" />
                            </video>
                        </div>
                        <div className="w-full lg:w-2/3 order-1 lg:order-2 grid md:grid-cols-2 gap-8">
                            {[
                                { title: "Industry Specific", desc: "No irrelevant listings. Built strictly for mining.", icon: "fa-crosshairs" },
                                { title: "Verified Users", desc: "Trade with confidence with vetted buyers and sellers.", icon: "fa-user-shield" },
                                { title: "Global Visibility", desc: "Suppliers reach a wider, targeted audience.", icon: "fa-globe-africa" },
                                { title: "Faster Sourcing", desc: "Reduce lead times with direct connections.", icon: "fa-tachometer-alt" },
                                { title: "Secure Transactions", desc: "Structured processes to protect your funds.", icon: "fa-lock" },
                                { title: "Purpose Built", desc: "Not a retail platform adapted for industry.", icon: "fa-hard-hat" }
                            ].map((benefit, idx) => (
                                <FadeInSection key={idx} delay={`${idx * 100}ms`}>
                                    <div className="bg-gray-800 p-6 rounded-xl hover:bg-gray-750 transition-all hover:-translate-y-1 border-t-2 border-transparent hover:border-blue-500 h-full">
                                        <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mb-4 text-blue-400">
                                            <i className={`fas ${benefit.icon} text-xl`}></i>
                                        </div>
                                        <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                                        <p className="text-gray-400">{benefit.desc}</p>
                                    </div>
                                </FadeInSection>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* 9. HOW IT WORKS (ID: how-it-works) */}
            <section id="how-it-works" className="py-24 bg-gray-800">
                <div className="container mx-auto px-4">
                    <FadeInSection>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-16 text-center">How Miners Market Works</h2>
                    </FadeInSection>

                    <div className="grid md:grid-cols-2 gap-12">
                        <FadeInSection delay="100ms">
                            <div className="bg-gray-900 p-8 rounded-2xl border border-gray-700 h-full">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-12 h-12 rounded-full bg-blue-900 text-blue-400 flex items-center justify-center font-bold text-xl">B</div>
                                    <h3 className="text-3xl font-bold text-white">For Buyers</h3>
                                </div>
                                <ul className="space-y-6">
                                    {[
                                        "Browse or search mining-specific categories",
                                        "Compare listings from verified sellers",
                                        "Engage directly or request quotes",
                                        "Transact with confidence"
                                    ].map((step, i) => (
                                        <li key={i} className="flex items-start gap-4">
                                            <i className="fas fa-check text-blue-500 mt-1 text-xl"></i>
                                            <span className="text-gray-300 text-lg">{step}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </FadeInSection>

                        <FadeInSection delay="300ms">
                            <div className="bg-gray-900 p-8 rounded-2xl border border-gray-700 h-full">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-12 h-12 rounded-full bg-blue-900 text-blue-400 flex items-center justify-center font-bold text-xl">S</div>
                                    <h3 className="text-3xl font-bold text-white">For Sellers</h3>
                                </div>
                                <ul className="space-y-6">
                                    {[
                                        "List equipment, parts, ores, or services",
                                        "Upload supporting documentation",
                                        "Reach targeted mining buyers",
                                        "Manage enquiries and offers"
                                    ].map((step, i) => (
                                        <li key={i} className="flex items-start gap-4">
                                            <i className="fas fa-check text-blue-500 mt-1 text-xl"></i>
                                            <span className="text-gray-300 text-lg">{step}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </FadeInSection>
                    </div>
                </div>
            </section>

            {/* 10. CALL TO ACTION */}
            <section className="py-24 bg-blue-900 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('images/backgrounds/home-bg.jpg')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
                <div className="container mx-auto px-4 relative z-10 text-center">
                    <FadeInSection>
                        <h2 className="text-5xl md:text-6xl font-bold text-white mb-10">Access Miners Market Today</h2>
                        <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
                            <button 
                                onClick={onLogin}
                                className="bg-white text-blue-900 hover:bg-gray-100 font-bold py-5 px-12 rounded-lg text-xl shadow-xl transition-transform transform hover:-translate-y-1 w-full md:w-auto"
                            >
                                Use Miners Market on Web
                            </button>
                            <div className="flex gap-4 w-full md:w-auto justify-center">
                                <button
                                    onClick={() => openExternalLink('https://apps.apple.com/za/app/miners-market/id6753230270')}
                                    className="bg-gray-900 text-white hover:bg-gray-800 py-4 px-8 rounded-lg flex items-center gap-3 transition-colors border border-gray-700"
                                >
                                    <i className="fab fa-apple text-2xl"></i>
                                    <div className="text-left">
                                        <div className="text-xs text-gray-400">Download on the</div>
                                        <div className="text-sm font-bold">App Store</div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => openExternalLink('https://play.google.com/store/apps/details?id=za.co.minersmarket.twa&hl=en-US&pli=1')}
                                    className="bg-gray-900 text-white hover:bg-gray-800 py-4 px-8 rounded-lg flex items-center gap-3 transition-colors border border-gray-700"
                                >
                                    <i className="fab fa-google-play text-2xl"></i>
                                    <div className="text-left">
                                        <div className="text-xs text-gray-400">Get it on</div>
                                        <div className="text-sm font-bold">Google Play</div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </FadeInSection>
                </div>
            </section>

            {/* 11. FOOTER (ID: contact) */}
            <footer id="contact" className="bg-gray-950 text-gray-400 py-16 border-t border-gray-900">
                <div className="container mx-auto px-4">
                    <div className="mb-12">
                        <img src="images/backgrounds/FooterCommunitySocial.jpg" alt="Join the Community" className="w-full h-64 object-cover rounded-2xl opacity-50 hover:opacity-80 transition-opacity" />
                    </div>
                    
                    <div className="grid md:grid-cols-4 gap-12 mb-12">
                        <div className="col-span-1 md:col-span-2">
                            <div className="flex items-center gap-3 mb-6">
                                <img src="images/logo/MM Logo.jpg" alt="Miners Market" className="h-12 w-12 rounded-lg" />
                                <span className="text-2xl font-bold text-white">MINERS MARKET</span>
                            </div>
                            <p className="text-gray-500 max-w-md mb-6">
                                The premier digital marketplace for the mining industry. Connecting buyers and sellers with transparency, security, and efficiency.
                            </p>
                            <div className="flex gap-4">
                                <a href="#" className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><i className="fab fa-linkedin-in"></i></a>
                                <a href="#" className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center hover:bg-blue-500 hover:text-white transition-colors"><i className="fab fa-facebook-f"></i></a>
                                <a href="#" className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center hover:bg-green-500 hover:text-white transition-colors"><i className="fab fa-whatsapp"></i></a>
                            </div>
                        </div>
                        
                        <div>
                            <h4 className="text-white font-bold mb-6">Platform</h4>
                            <ul className="space-y-4 text-sm">
                                <li><button onClick={onLogin} className="hover:text-blue-400 transition-colors">Login</button></li>
                                <li><button onClick={onSignUp} className="hover:text-blue-400 transition-colors">Create Account</button></li>
                                <li><a href="#" className="hover:text-blue-400 transition-colors">Browse Equipment</a></li>
                                <li><a href="#" className="hover:text-blue-400 transition-colors">Sell on Miners Market</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-white font-bold mb-6">Legal</h4>
                            <ul className="space-y-4 text-sm">
                                <li><a href="/miners-market-main/documents/footer/Miners Market Privacy Policy.pdf" target="_blank" className="hover:text-blue-400 transition-colors">Privacy Policy</a></li>
                                <li><a href="/miners-market-main/documents/footer/Miners Market Terms of Use.pdf" target="_blank" className="hover:text-blue-400 transition-colors">Terms of Use</a></li>
                                <li><a href="#" className="hover:text-blue-400 transition-colors">Disclaimer</a></li>
                            </ul>
                        </div>
                    </div>
                    
                    <div className="border-t border-gray-900 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-600">
                        <p>&copy; {new Date().getFullYear()} Miners Market. All rights reserved.</p>
                        <p>Powered by Miners Market</p>
                    </div>
                </div>
            </footer>

            {/* --- NEW: Render the confirmation modal --- */}
            <UseWebVersionModal 
                isOpen={showWebConfirm}
                onClose={() => setShowWebConfirm(false)}
                onConfirm={() => {
                    setShowWebConfirm(false);
                    navigate('/equipment');
                }}
            />
        </div>
    );
}
